import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildLocalizedPath, stripLangFromPath } from '../lib/i18nRouting';

const languages = [
  { code: 'fr', name: 'Français', shortName: 'FR', flag: '🇫🇷' },
  { code: 'en', name: 'English',  shortName: 'EN', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch',  shortName: 'DE', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', shortName: 'IT', flag: '🇮🇹' },
  { code: 'es', name: 'Español',  shortName: 'ES', flag: '🇪🇸' }
];

// Routes for which we use URL-based language switching (SEO-strategic pages)
const SEO_PAGES_REGEX = /^(\/(fr|en|de|it|es))?(\/blog(\/.*)?)?\/?$/;

export const LanguageSelector = ({ variant = 'default' }) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = (code) => {
    // Sur les pages SEO-stratégiques (landing, blog), navigue vers la version
    // localisée de l'URL pour que Google indexe chaque langue séparément.
    const cleanPath = stripLangFromPath(location.pathname);
    const isSeoPage = SEO_PAGES_REGEX.test(cleanPath);

    if (isSeoPage) {
      const target = buildLocalizedPath(cleanPath, code) + location.search + location.hash;
      navigate(target);
      // i18n se synchronise automatiquement via useSyncLanguageFromURL
    } else {
      i18n.changeLanguage(code);
    }
    setIsOpen(false);
  };

  if (variant === 'compact') {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-300 hover:text-white hover:bg-white/10 rounded-md transition-colors border border-white/15"
          data-testid="language-selector"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className="text-base leading-none">{currentLang.flag}</span>
          <span className="hidden sm:inline text-xs font-medium tracking-wide">{currentLang.shortName}</span>
          <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 bg-white rounded-lg shadow-xl border border-slate-200 py-1.5 z-50 min-w-[160px]" role="listbox">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2.5 hover:bg-slate-100 transition-colors ${
                  lang.code === i18n.language ? 'bg-sky-50 text-sky-700 font-medium' : 'text-slate-700'
                }`}
                data-testid={`lang-option-${lang.code}`}
                role="option"
                aria-selected={lang.code === i18n.language}
              >
                <span className="text-base leading-none">{lang.flag}</span>
                <span className="flex-1">{lang.name}</span>
                {lang.code === i18n.language && <span className="text-sky-600">✓</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors border border-slate-200"
        data-testid="language-selector"
      >
        <Globe className="w-4 h-4" />
        <span>{currentLang.flag}</span>
        <span>{currentLang.name}</span>
      </button>
      
      {isOpen && (
        <div className="absolute left-0 bottom-full mb-2 bg-white rounded-lg shadow-xl border border-slate-200 py-2 z-50 min-w-[160px]">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 hover:bg-slate-100 transition-colors ${
                lang.code === i18n.language ? 'bg-sky-50 text-sky-700 font-medium' : 'text-slate-700'
              }`}
              data-testid={`lang-option-${lang.code}`}
            >
              <span className="text-lg">{lang.flag}</span>
              <span>{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
